import {
    Calculation,
    LoanApplication,
    ResidenceOwnership2,
    SubmittingUser,
    PropertyType,
} from '@servicetitan/financing-api-client';
import { ApplicationData } from './ApplicationDataStore';
import { formatAmount, PartialDeep } from '@servicetitan/financing-utils-shared';
import { calculateIncomes } from '../../utils/calculateIncomes';
import { preparePrimaryApplicantSpouseData, prepareCoApplicantSpouseData } from '../spouseInfo';

export const prepareApplicationData = ({
    applicationData: originalApplicationData,
    selectedPlan,
    saleAmount,
    downPayment,
    financedAmount,
    tenantId,
    providerName,
    buId,
    technicianId,
    customerId,
    jobId,
    fingerPrint,
    applicantSignature,
    coApplicantSignature,
    disclosureToken,
    submittingUser,
    hasMailingAddress,
    referrer,
}: {
    applicationData: ApplicationData;
    selectedPlan: Calculation;
    saleAmount: number;
    downPayment: number;
    financedAmount: number;
    tenantId: string;
    providerName: string;
    buId: string;
    technicianId: string;
    customerId: string;
    jobId: string | null;
    fingerPrint?: string;
    applicantSignature?: string;
    coApplicantSignature?: string;
    disclosureToken?: string;
    submittingUser?: SubmittingUser;
    hasMailingAddress?: boolean;
    referrer?: string;
}) => {
    // Deep clone the application data to avoid modifying the original
    const applicationData = JSON.parse(JSON.stringify(originalApplicationData));

    // Use mailing address if available, otherwise use jobDetails.address
    const homeAddress = hasMailingAddress
        ? applicationData.mailingAddress.primaryApplicant?.address
        : applicationData.applicant.primaryApplicant?.jobDetails?.address;

    const propertyAddress = hasMailingAddress
        ? applicationData.mailingAddress.workPerformedAtAddress
            ? applicationData.mailingAddress.primaryApplicant?.address
            : applicationData.serviceProperty.address
        : applicationData.applicant.primaryApplicant?.jobDetails?.address;

    const hasCoApplicant = applicationData.coApplicant.hasCoApplicant;
    delete applicationData.coApplicant.hasCoApplicant;

    const isAddressSameAsPrimary =
        hasCoApplicant && applicationData.coApplicant.coApplicant?.isAddressSameAsPrimary;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    hasCoApplicant && delete applicationData.coApplicant.coApplicant?.isAddressSameAsPrimary;

    // FIN-15610 - sometimes number fields are formatted as strings, seems limited to Safari and Mobile Safari
    // Calculate monthlyHousingPayment based on housing type and available data
    // For simultaneous flows (ApplicationWithCoApplicationWorkflow), mortgage info comes from jobDetails
    // For regular flows (ApplicationWorkflow), it comes from serviceProperty
    const jobDetailsResidenceType =
        applicationData.applicant.primaryApplicant?.jobDetails?.residenceType;
    const servicePropertyHousingType =
        applicationData.serviceProperty.primaryApplicant?.housingType;

    let monthlyHousingPayment = 0;

    // Determine housing payment based on residence/housing type
    if (jobDetailsResidenceType === ResidenceOwnership2.OwnWithMortgage) {
        // From jobDetails (simultaneous flow)
        monthlyHousingPayment =
            applicationData.applicant.primaryApplicant?.jobDetails?.mortgageMonthlyPayment ?? 0;
    } else if (servicePropertyHousingType === ResidenceOwnership2.Own) {
        // From serviceProperty (regular flow)
        monthlyHousingPayment =
            applicationData.serviceProperty.primaryApplicant?.monthlyHousingPayment ?? 0;
    } else if (jobDetailsResidenceType === ResidenceOwnership2.Own) {
        // Own outright - no mortgage payment
        monthlyHousingPayment = 0;
    } else if (
        jobDetailsResidenceType === ResidenceOwnership2.Rent ||
        servicePropertyHousingType === ResidenceOwnership2.Rent
    ) {
        // Rent - would come from serviceProperty if available, otherwise 0
        monthlyHousingPayment =
            applicationData.serviceProperty.primaryApplicant?.monthlyHousingPayment ?? 0;
    }

    // Handle string-formatted numbers
    monthlyHousingPayment =
        typeof monthlyHousingPayment === 'string'
            ? Number(formatAmount(monthlyHousingPayment))
            : monthlyHousingPayment;

    const { grossMonthlyIncome, annualIncome } = calculateIncomes({
        grossMonthlyIncome:
            applicationData.applicant.primaryApplicant?.employment?.grossMonthlyIncome,
        annualIncome: applicationData.applicant.primaryApplicant?.annualIncome,
    });

    const { grossMonthlyIncome: coGrossMonthlyIncome, annualIncome: coGrossAnnualIncome } =
        calculateIncomes({
            grossMonthlyIncome:
                applicationData.coApplicant.coApplicant?.employment?.grossMonthlyIncome,
            annualIncome: applicationData.coApplicant.coApplicant?.annualIncome,
        });

    const { jobDetails, email, typeOfHome, ...cleanPrimaryApplicant } =
        applicationData.applicant.primaryApplicant ?? {};

    const {
        jobDetails: coApplicantJobDetails,
        email: coApplicantEmail,
        typeOfHome: coApplicantTypeOfHome,
        spouseFirstName,
        spouseLastName,
        ...cleanCoApplicant
    } = applicationData.coApplicant.coApplicant ?? {};

    const coApplicantAddress =
        isAddressSameAsPrimary === true
            ? homeAddress
            : hasMailingAddress
              ? applicationData.mailingAddress.coApplicant?.address
              : (coApplicantJobDetails?.address ??
                applicationData.coApplicant.coApplicant?.address);

    // Derive propertyType from jobDetails.isManufacturedHome when serviceProperty is not available
    // This handles the co-application flow where JobDetailsContainer is used instead of ServicePropertyContainer
    const propertyType =
        applicationData.serviceProperty?.primaryApplicant?.propertyType ??
        (jobDetails?.isManufacturedHome === true
            ? PropertyType.ManufacturedHome
            : jobDetails?.isManufacturedHome === false
              ? PropertyType.SingleFamily
              : undefined);

    // Get improvement from either serviceProperty (regular flow) or applicant (co-application flow)
    const improvement =
        applicationData.serviceProperty?.improvement ??
        (applicationData.applicant as any)?.improvement;

    const finalApplication: PartialDeep<LoanApplication> = {
        primaryApplicant: {
            ...cleanPrimaryApplicant,
            ...applicationData.serviceProperty.primaryApplicant!,
            propertyType,
            employment: {
                ...applicationData.applicant.primaryApplicant!.employment,
                grossMonthlyIncome,
            },
            emailAddress: email,
            signature: applicantSignature?.trim(),
            address: homeAddress,
            annualIncome,
            housingType:
                jobDetails?.residenceType ??
                applicationData.serviceProperty.primaryApplicant?.housingType,
            residenceType: typeOfHome,
            employmentStatus: applicationData.applicant.primaryApplicant?.employment?.status,
            ...preparePrimaryApplicantSpouseData(applicationData, homeAddress),
            isUSCitizen: applicationData.applicant.primaryApplicant?.isUSCitizen
                ? applicationData.applicant.primaryApplicant.isUSCitizen === 'true'
                : undefined,
        },
        coApplicant: hasCoApplicant
            ? {
                  ...cleanCoApplicant,
                  emailAddress: coApplicantEmail,
                  annualIncome: coGrossAnnualIncome,
                  housingType: jobDetails?.residenceType,
                  residenceType: coApplicantTypeOfHome,
                  employmentStatus: applicationData.coApplicant.coApplicant?.employment?.status,
                  signature: coApplicantSignature?.trim(),
                  isUSCitizen: applicationData.coApplicant.coApplicant?.isUSCitizen
                      ? applicationData.coApplicant.coApplicant.isUSCitizen === 'true'
                      : undefined,
                  employment: {
                      ...applicationData.coApplicant.coApplicant?.employment,
                      grossMonthlyIncome: coGrossMonthlyIncome,
                  },
                  ...prepareCoApplicantSpouseData(
                      applicationData,
                      coApplicantJobDetails,
                      coApplicantAddress,
                  ),
              }
            : undefined,
        salesAmount: Math.round(saleAmount * 100) / 100,
        requestedAmount: Math.round(financedAmount * 100) / 100,
        planNumber: selectedPlan.planNumber,
        downPayment: Math.round(downPayment * 100) / 100,
        tenantId,
        providerName,
        buId,
        technicianId,
        customerId,
        jobId,
        improvement,
        source: applicationData.source,
        referrer,
        address: propertyAddress,
        fingerPrint,
        disclosureToken,
        technicianLastName: submittingUser?.lastName,
        technicianPhone: submittingUser?.phone,
        technicianEmail: submittingUser?.email,
        mortgageBalance:
            applicationData.applicant.primaryApplicant?.jobDetails?.residenceType ===
            ResidenceOwnership2.OwnWithMortgage
                ? applicationData.applicant.primaryApplicant?.jobDetails?.mortgageEstimatedBalance
                : undefined,
        mortgagePayment:
            applicationData.applicant.primaryApplicant?.jobDetails?.residenceType ===
            ResidenceOwnership2.OwnWithMortgage
                ? applicationData.applicant.primaryApplicant?.jobDetails?.mortgageMonthlyPayment
                : undefined,
    };

    const hasOwnership =
        finalApplication.primaryApplicant?.housingType === ResidenceOwnership2.Own ||
        finalApplication.primaryApplicant?.housingType === ResidenceOwnership2.OwnWithMortgage;

    if (hasOwnership) {
        finalApplication.primaryApplicant!.monthlyHousingPayment = monthlyHousingPayment;
    }

    finalApplication.primaryApplicant!.ssn = finalApplication.primaryApplicant?.ssn?.replace(
        /-/g,
        '',
    );

    finalApplication.primaryApplicant!.firstName =
        finalApplication.primaryApplicant?.firstName?.trim();
    finalApplication.primaryApplicant!.lastName =
        finalApplication.primaryApplicant?.lastName?.trim();

    finalApplication.primaryApplicant!.primaryPhone =
        finalApplication.primaryApplicant?.primaryPhone?.replace(/[\(\)\s-]/g, '');

    if (hasCoApplicant) {
        finalApplication.coApplicant!.primaryPhone =
            finalApplication.coApplicant?.primaryPhone?.replace(/[\(\)\s-]/g, '');
        finalApplication.coApplicant!.ssn = finalApplication.coApplicant?.ssn?.replace(/-/g, '');
        finalApplication.coApplicant!.firstName = finalApplication.coApplicant?.firstName?.trim();
        finalApplication.coApplicant!.lastName = finalApplication.coApplicant?.lastName?.trim();
    }

    if (hasCoApplicant && isAddressSameAsPrimary === true) {
        finalApplication.coApplicant!.address = homeAddress;
    }

    return finalApplication;
};
